import { FormCheckboxGroup, useZionFormContext } from '@fs/zion-form'
import { ControlSplit } from '@fs/zion-icon'
import { Button, Divider, Separator } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSlsStateContext } from '@fs/zion-sls'
import SelectPersonsPrefixedCheckbox from './SelectPersonsPrefixedCheckbox'
import { useSharedBrowserStateContext } from '../../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

const RECORD_PREFIX = 'selectedInRecord'
const IMAGE_PREFIX = 'selectedInImage'
const OTHER_ARKS_PREFIX = 'selectedInOtherArks'

/**
 * Callback function for the View In Context button.
 *
 * @callback viewInContextHandler
 * @param {Object[]} pageNumber - image index + 1
 */

/**
 * EventPersonSelectionGroups component for displaying checkboxes for three categories: in this record, in this image, and in other arks
 * @param {Array} personsInThisRecord - Array of persons in the current record
 * @param {Array} personsInThisImage - Array of persons in the current image
 * @param {Object} allTheArksExceptCurrent - Object containing keys for all other relevant arks
 * @param {Object} selectedPerson - the selected person
 * @param {Function} viewInContextHandler - called with pageNumber. Should display the corresponding image.
 * @returns {Element}
 */
export default function RecordAndImageCheckboxes({
  personsInThisRecord,
  personsInThisImage,
  allTheArksExceptCurrent,
  selectedPerson,
  viewInContextHandler,
}) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const methods = useZionFormContext()
  const { thumbnails } = useSharedBrowserStateContext()

  const currentArkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === zionSlsState.currentImageArk)
  const checkboxGroups = [RECORD_PREFIX, IMAGE_PREFIX, OTHER_ARKS_PREFIX]

  const onChange = (personId, namePrefix) => {
    const values = methods.getValues()
    const newValue = values[namePrefix][personId]

    checkboxGroups.forEach((checkboxGroup) => {
      if (checkboxGroup !== namePrefix && typeof values[checkboxGroup][personId] !== 'undefined') {
        methods.setValue(`${checkboxGroup}.${personId}`, newValue)
      }
    })
  }

  return (
    <>
      <FormCheckboxGroup
        label={t('record-image-checkboxes.in-record', 'In this Record')}
        data-testid="in-this-record-checkbox-group"
      >
        {personsInThisRecord.map((person) => (
          <SelectPersonsPrefixedCheckbox
            key={`inThisRecord.${person.id}`}
            person={person}
            namePrefix={RECORD_PREFIX}
            onChange={onChange}
            selectedPerson={selectedPerson}
          />
        ))}
      </FormCheckboxGroup>
      <FormCheckboxGroup
        label={t('record-image-checkboxes.in-image', 'In this Image')}
        data-testid="in-this-image-checkbox-group"
      >
        {personsInThisImage.map((person) => (
          <SelectPersonsPrefixedCheckbox
            key={`inThisImage.${person.id}`}
            person={person}
            namePrefix={IMAGE_PREFIX}
            onChange={onChange}
            selectedPerson={selectedPerson}
          />
        ))}
      </FormCheckboxGroup>
      {allTheArksExceptCurrent &&
        Object.entries(allTheArksExceptCurrent)
          // This filter statement keeps us from attempting to load a page that isnt directly adjacent to current
          // This is necessary, because as of now, we don't load any other pages into state, so we don't have their data
          .filter(([ark]) => {
            const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
            return Math.abs(currentArkIndex - arkIndex) === 1
          })
          .map(([ark, obj]) => {
            // Shouldn't this be object notation?
            const arkIndex = thumbnails?.findIndex((thumbnail) => thumbnail.ark === ark)
            const pageNumber = arkIndex + 1
            return (
              <div key={pageNumber}>
                <Separator size="xs" />
                <Divider
                  label={t('record-image-checkboxes.names-on-page-n.label', 'Names on Page {pageNumber}', {
                    pageNumber,
                  })}
                />
                <Button Icon={ControlSplit} onClick={() => viewInContextHandler(pageNumber)} emphasis="medium">
                  {t('record-image-checkboxes.view-image-in-context.close.button', 'View in Context')}
                </Button>
                {obj.display?.map((person) => (
                  <SelectPersonsPrefixedCheckbox
                    key={`inOtherArks.${person.id}`}
                    person={person}
                    namePrefix={OTHER_ARKS_PREFIX}
                    onChange={onChange}
                    selectedPerson={selectedPerson}
                  />
                ))}
              </div>
            )
          })}
    </>
  )
}
